import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { useMsal } from '@azure/msal-react';
import { authService, setAuthToken } from '../services/apiService';

// Permission levels are hardcoded on the backend (EmailAuthorizationFilter):
// ADMIN has full access, VIEWER is read-only and cannot update settings.
// accessDenied is true when the API refuses the signed-in Microsoft account altogether (403), and
// error holds any other failure of the check, which reload() retries.
const PermissionsContext = createContext({
  role: null,
  canEdit: false,
  loading: true,
  accessDenied: false,
  error: null,
  reload: () => {},
});

const NOT_CHECKED = { accountKey: null, role: null, accessDenied: false, error: null };

export const PermissionsProvider = ({ children }) => {
  const { instance, accounts } = useMsal();
  const accountKey = accounts[0]?.homeAccountId ?? null;
  // The latest account list, read inside the check without re-running it whenever MSAL republishes it
  const accountsRef = useRef(accounts);
  accountsRef.current = accounts;
  // The outcome of the last check and the account it was for, so a newly signed-in account never
  // reads as checked before its own request has finished
  const [check, setCheck] = useState(NOT_CHECKED);
  const [checking, setChecking] = useState(false);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!accountKey) {
      setCheck(NOT_CHECKED);
      return undefined;
    }
    let cancelled = false;
    const fetchRole = async () => {
      setChecking(true);
      try {
        const request = {
          scopes: ['api://7e9ba977-33b0-4e9c-ae75-55f65da317bd/access'],
          account: accountsRef.current[0],
        };
        const response = await instance.acquireTokenSilent(request);
        setAuthToken(response.accessToken);
        const me = await authService.getCurrentUser();
        if (!cancelled) setCheck({ accountKey, role: me.data.role || null, accessDenied: false, error: null });
      } catch (err) {
        console.error('Failed to load permissions', err);
        if (cancelled) return;
        const denied = err?.response?.status === 403;
        setCheck({ accountKey, role: null, accessDenied: denied, error: denied ? null : err });
      } finally {
        if (!cancelled) setChecking(false);
      }
    };
    fetchRole();
    return () => { cancelled = true; };
  }, [instance, accountKey, attempt]);

  const reload = useCallback(() => setAttempt((n) => n + 1), []);

  const current = accountKey !== null && check.accountKey === accountKey ? check : NOT_CHECKED;
  const loading = accountKey !== null && (check.accountKey !== accountKey || checking);
  const canEdit = current.role === 'ADMIN';

  return (
    <PermissionsContext.Provider
      value={{
        role: current.role,
        canEdit,
        loading,
        accessDenied: current.accessDenied,
        error: current.error,
        reload,
      }}
    >
      {children}
    </PermissionsContext.Provider>
  );
};

export const usePermissions = () => useContext(PermissionsContext);
